// src/components/dashboard/ClassicLensBackground.tsx
// Vintage color-adjustable surface + lens + parallax (app-wide).
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LIGHT_BACKGROUND_KEYS } from '../../utils/backgrounds';
import { mountClassicLensBackground } from './classicLensBackgroundEngine';
import ExperimentalSideVignette from './ExperimentalSideVignette';

export type ClassicLensColorKey =
  | 'icy-blue'
  | 'soft-mint'
  | 'soft-lavender'
  | 'soft-peach'
  | 'soft-sage'
  | 'soft-sky'
  | 'soft-rose'
  | 'soft-sand';

const BASE_FALLBACK: Record<ClassicLensColorKey, string> = {
  'icy-blue': '#8fc4d6',
  'soft-mint': '#8fd0bd',
  'soft-lavender': '#b9aad4',
  'soft-peach': '#e0bc9c',
  'soft-sage': '#9cbcab',
  'soft-sky': '#72c0db',
  'soft-rose': '#d6a8b4',
  'soft-sand': '#d4c29c',
};

const COLOR_KEYS = new Set<string>(LIGHT_BACKGROUND_KEYS);

const readStoredColorKey = (): ClassicLensColorKey => {
  for (const storageKey of ['dashboard_selected_entity', 'ventaje_leads_selected_entity']) {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const key = parsed?.colorKey;
      if (typeof key === 'string' && COLOR_KEYS.has(key)) return key as ClassicLensColorKey;
    } catch {
      /* keep looking */
    }
  }
  return 'icy-blue';
};

interface ClassicLensBackgroundProps {
  colorKey?: ClassicLensColorKey;
  getScrollElement?: () => HTMLElement | null;
}

const ClassicLensBackground: React.FC<ClassicLensBackgroundProps> = ({
  colorKey: colorKeyProp,
  getScrollElement,
}) => {
  const location = useLocation();
  const [liveKey, setLiveKey] = useState<ClassicLensColorKey>(readStoredColorKey);
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768,
  );
  const colorKey = colorKeyProp || liveKey;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const handleRef = useRef<ReturnType<typeof mountClassicLensBackground> | null>(null);

  useEffect(() => {
    if (colorKeyProp) return;
    const onColor = (event: Event) => {
      const key = (event as CustomEvent<string>).detail;
      if (typeof key === 'string' && COLOR_KEYS.has(key)) {
        setLiveKey(key as ClassicLensColorKey);
      }
    };
    window.addEventListener('ventaje:background-color', onColor);
    return () => window.removeEventListener('ventaje:background-color', onColor);
  }, [colorKeyProp]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handle = mountClassicLensBackground(canvas, {
      preset: {
        colorKey,
        lensX: 0.12,
        lensY: 0.38,
        parallax: 0.18,
      },
      getScrollElement:
        getScrollElement ||
        (() => document.querySelector('[data-exp-scroll]') as HTMLElement | null),
    });
    handleRef.current = handle;

    const retry = window.setTimeout(() => handle.rebindScroll?.(), 400);

    return () => {
      window.clearTimeout(retry);
      handle.destroy();
      handleRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- colorKey is applied by the next effect (handleRef.current?.apply), never by remounting the engine (WU-141 D8)
  }, [getScrollElement]);

  useEffect(() => {
    handleRef.current?.apply({ colorKey });
  }, [colorKey]);

  useEffect(() => {
    handleRef.current?.rebindScroll();
    handleRef.current?.redraw();
  }, [location.pathname]);

  return (
    <div
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ background: BASE_FALLBACK[colorKey] || BASE_FALLBACK['icy-blue'] }}
      aria-hidden
    >
      <canvas
        ref={canvasRef}
        className="block w-full h-full"
        style={{ opacity: 0, transition: 'opacity 0.55s ease' }}
      />
      {location.pathname.startsWith('/workspace') || narrow ? null : (
        <ExperimentalSideVignette edge={location.pathname.startsWith('/leads') ? 'left' : 'both'} />
      )}
    </div>
  );
};

export default ClassicLensBackground;
